import { useSuspenseQuery } from '@apollo/client';
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import { useApiClient } from '@eluve/api-client-provider';
import {
  OPTIONAL_HEADERS,
  type OptionalHeader,
  type PatientImportKnownSourceKey,
  type PatientImportSource,
  REQUIRED_HEADERS,
  type RequiredHeader,
} from '@eluve/api-contract';
import { eluveAdminHasuraContext } from '@eluve/apollo-client';
import {
  Button,
  Combobox,
  ComboboxDropdown,
  ComboboxEmpty,
  ComboboxOption,
  ComboboxSelectButton,
  Heading,
  Icon,
  Input,
  Separator,
  Stack,
  Text,
  cn,
  useToast,
} from '@eluve/components';
import { calculateFileSha256 } from '@eluve/utils';

import { FlowStepper } from './components/FlowStepper';
import { LabeledField } from './components/LabeledField';
import { WhiteCard } from './components/WhiteCard';
import { listTenantsForPickerQuery } from './patient-import.operations';
import {
  PATIENT_IMPORT_ROUTE,
  TEMPLATE_FILENAME,
  downloadTemplateCsv,
  errMsg,
} from './utils';

const REQUIRED_HINTS: Record<RequiredHeader, string> = {
  patient_id: 'Source patient ID',
  first_name: '',
  last_name: '',
  date_of_birth: 'YYYY-MM-DD or MM/DD/YYYY',
};

const OPTIONAL_HINTS: Record<OptionalHeader, string> = {
  email: 'valid email format',
  phone_number: '10 digits',
};

const REQUIRED_COLUMNS = REQUIRED_HEADERS.map((key) => ({
  key,
  hint: REQUIRED_HINTS[key],
}));
const OPTIONAL_COLUMNS = OPTIONAL_HEADERS.map((key) => ({
  key,
  hint: OPTIONAL_HINTS[key],
}));

const formatBytes = (bytes: number): string => {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
};

const ColumnRow: React.FC<{ name: string; hint: string }> = ({
  name,
  hint,
}) => (
  <Stack direction="horizontal" justify="between" align="baseline" gap={3}>
    <Text inline size="sm" className="shrink-0 font-mono">
      {name}
    </Text>
    <Text size="xs" color="tertiary" className="text-right">
      {hint}
    </Text>
  </Stack>
);

type SourceChoice =
  | {
      type: 'KNOWN';
      sourceKey: PatientImportKnownSourceKey;
      displayName: string;
    }
  | {
      type: 'CUSTOM';
      displayName: string;
    };

type SourceOption =
  | {
      kind: 'source';
      id: string;
      displayName: string;
      choice: SourceChoice;
    }
  | {
      kind: 'other';
      id: 'OTHER';
      displayName: string;
      choice: null;
    };

const SourceSelect: React.FC<{
  tenantId: string;
  value: SourceChoice | null;
  onChange: (source: SourceChoice | null) => void;
  disabled?: boolean;
  errorText?: string;
}> = ({ tenantId, value, onChange, disabled, errorText }) => {
  const apiClient = useApiClient();
  const [knownSources, setKnownSources] = useState<
    Array<{ sourceKey: PatientImportKnownSourceKey; displayName: string }>
  >([]);
  const [tenantSources, setTenantSources] = useState<PatientImportSource[]>([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [customOpen, setCustomOpen] = useState(false);
  const [customName, setCustomName] = useState('');

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setLoadError(null);
    setKnownSources([]);
    setTenantSources([]);

    apiClient.patientImport
      .listImportSources({ params: { tenantId } })
      .then((res) => {
        if (cancelled) return;
        if (res.status !== 200) {
          throw new Error(`Failed to load sources (${res.status})`);
        }
        setKnownSources(res.body.knownSources);
        setTenantSources(res.body.tenantSources);
      })
      .catch((err: unknown) => {
        if (cancelled) return;
        setLoadError(errMsg(err));
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [apiClient, tenantId]);

  const options = useMemo<SourceOption[]>(
    () => [
      ...knownSources.map((source) => ({
        kind: 'source' as const,
        id: `KNOWN:${source.sourceKey}`,
        displayName: source.displayName,
        choice: {
          type: 'KNOWN' as const,
          sourceKey: source.sourceKey,
          displayName: source.displayName,
        },
      })),
      ...tenantSources.map((source) => ({
        kind: 'source' as const,
        id: `CUSTOM:${source.id}`,
        displayName: source.displayName,
        choice: {
          type: 'CUSTOM' as const,
          displayName: source.displayName,
        },
      })),
      {
        kind: 'other' as const,
        id: 'OTHER' as const,
        displayName: 'Other source…',
        choice: null,
      },
    ],
    [knownSources, tenantSources],
  );

  const selectedLabel =
    value?.displayName ??
    (customOpen && customName.trim() ? customName.trim() : 'Select source…');

  const onCustomNameChange = (next: string) => {
    setCustomName(next);
    const displayName = next.trim().replace(/\s+/g, ' ');
    onChange(displayName ? { type: 'CUSTOM', displayName } : null);
  };

  return (
    <LabeledField label="Source" error={errorText ?? loadError}>
      <Stack gap={2}>
        <Combobox disabled={disabled || loading}>
          <ComboboxSelectButton className="w-full">
            {loading ? 'Loading sources…' : selectedLabel}
          </ComboboxSelectButton>
          <ComboboxDropdown searchPlaceholder="Search sources…">
            <ComboboxEmpty>No sources found</ComboboxEmpty>
            {options.map((option) => (
              <ComboboxOption
                key={option.id}
                searchValue={option.displayName}
                onSelect={() => {
                  if (option.kind === 'other') {
                    setCustomOpen(true);
                    setCustomName('');
                    onChange(null);
                    return;
                  }
                  setCustomOpen(false);
                  setCustomName('');
                  onChange(option.choice);
                }}
              >
                {option.displayName}
              </ComboboxOption>
            ))}
          </ComboboxDropdown>
        </Combobox>
        {customOpen ? (
          <Input
            value={customName}
            maxLength={120}
            disabled={disabled}
            placeholder="Type source name"
            onChange={(e) => onCustomNameChange(e.target.value)}
          />
        ) : null}
      </Stack>
    </LabeledField>
  );
};

const SourcePlaceholder: React.FC = () => (
  <LabeledField label="Source" error={null}>
    <Combobox disabled>
      <ComboboxSelectButton className="w-full">
        Pick an organization first
      </ComboboxSelectButton>
      <ComboboxDropdown searchPlaceholder="">
        <ComboboxEmpty>Pick an organization first</ComboboxEmpty>
      </ComboboxDropdown>
    </Combobox>
  </LabeledField>
);

export const CreatePatientImportPage: React.FC = () => {
  const apiClient = useApiClient();
  const toast = useToast();
  const navigate = useNavigate();
  const fileInputRef = useRef<HTMLInputElement>(null);

  const { data: tenantsData } = useSuspenseQuery(listTenantsForPickerQuery, {
    context: eluveAdminHasuraContext,
  });
  const tenants = tenantsData?.adminTenants ?? [];

  const [pickedTenantId, setPickedTenantId] = useState<string | null>(null);
  const tenantId = pickedTenantId;

  const [sourceChoice, setSourceChoice] = useState<SourceChoice | null>(null);
  const [file, setFile] = useState<File | null>(null);
  const [busy, setBusy] = useState(false);
  const [dragOver, setDragOver] = useState(false);
  const [showErrors, setShowErrors] = useState(false);

  const tenantLabel =
    tenants.find((t) => t.id === pickedTenantId)?.name ??
    'Select organization…';

  const canStart = Boolean(tenantId && sourceChoice && file);
  const errors = {
    tenant: !tenantId && showErrors,
    source: !sourceChoice && showErrors,
    file: !file && showErrors,
  };

  const onPickFile = (next: File | null) => {
    if (next && !next.name.toLowerCase().endsWith('.csv')) {
      toast.error('Only .csv files are supported');
      return;
    }
    setFile(next);
  };

  const onStart = async () => {
    if (!canStart || !tenantId || !sourceChoice || !file) {
      setShowErrors(true);
      return;
    }
    setBusy(true);
    try {
      const fileHash = await calculateFileSha256(file);

      const sourceRes = await apiClient.patientImport.resolveImportSource({
        params: { tenantId },
        body:
          sourceChoice.type === 'KNOWN'
            ? { type: 'KNOWN', sourceKey: sourceChoice.sourceKey }
            : { type: 'CUSTOM', displayName: sourceChoice.displayName },
      });
      if (sourceRes.status !== 200)
        throw new Error(`resolveImportSource failed (${sourceRes.status})`);
      const { sourceId: patientImportSourceId } = sourceRes.body;

      const prepRes = await apiClient.patientImport.prepareUpload({
        params: { tenantId },
        body: {
          patientImportSourceId,
          fileSize: file.size,
          fileName: file.name,
          fileHash,
        },
      });
      if (prepRes.status !== 201)
        throw new Error(`prepareUpload failed (${prepRes.status})`);
      const { uploadUrl, userUploadId } = prepRes.body;

      const putRes = await fetch(uploadUrl, { method: 'PUT', body: file });
      if (!putRes.ok)
        throw new Error(`Upload to GCS failed (${putRes.status})`);

      const startRes = await apiClient.patientImport.startImport({
        params: { tenantId },
        body: { patientImportSourceId, userUploadId, filename: file.name },
      });
      if (startRes.status !== 201)
        throw new Error(`startImport failed (${startRes.status})`);

      navigate(`${PATIENT_IMPORT_ROUTE}/${startRes.body.jobId}`);
    } catch (err) {
      toast.error(`Failed to start import: ${errMsg(err)}`);
    } finally {
      setBusy(false);
    }
  };

  return (
    <Stack gap={6} align="stretch" className="mx-auto max-w-7xl">
      <Stack gap={1}>
        <Heading>Bulk Import Patients</Heading>
        <Text color="secondary">
          Upload a CSV file of patient data to add them to Eluve.
        </Text>
      </Stack>

      <FlowStepper current="upload" />

      <div className="grid grid-cols-1 gap-6 lg:grid-cols-[1fr_380px]">
        <Stack gap={4} align="stretch">
          <WhiteCard>
            <Stack gap={4}>
              <Heading size="lg" weight="semibold">
                About This Import
              </Heading>

              <LabeledField
                label="Organization"
                error={
                  errors.tenant ? 'Select an organization to continue' : null
                }
              >
                <Combobox disabled={busy}>
                  <ComboboxSelectButton className="w-full">
                    {tenantLabel}
                  </ComboboxSelectButton>
                  <ComboboxDropdown searchPlaceholder="Search organizations…">
                    <ComboboxEmpty>No organizations</ComboboxEmpty>
                    {tenants.map((t) => (
                      <ComboboxOption
                        key={t.id}
                        searchValue={t.name ?? ''}
                        onSelect={() => {
                          setPickedTenantId(t.id);
                          setSourceChoice(null);
                        }}
                      >
                        {t.name ?? '—'}
                      </ComboboxOption>
                    ))}
                  </ComboboxDropdown>
                </Combobox>
              </LabeledField>

              {tenantId ? (
                <SourceSelect
                  tenantId={tenantId}
                  value={sourceChoice}
                  onChange={setSourceChoice}
                  disabled={busy}
                  errorText={
                    errors.source ? 'Select a source to continue' : undefined
                  }
                />
              ) : (
                <SourcePlaceholder />
              )}
            </Stack>
          </WhiteCard>

          <WhiteCard>
            <Stack gap={4}>
              <Heading size="lg" weight="semibold">
                Upload File
              </Heading>
              {file ? (
                <Stack
                  direction="horizontal"
                  justify="between"
                  align="center"
                  gap={3}
                  className="rounded-md border border-border-tertiary bg-bg-primary px-4 py-3"
                >
                  <Stack direction="horizontal" gap={3} align="center">
                    <div className="flex size-9 items-center justify-center rounded-md border border-border-tertiary bg-bg-secondary">
                      <Icon name="FileText" size="sm" />
                    </div>
                    <Stack gap={0}>
                      <Text size="sm" weight="medium">
                        {file.name}
                      </Text>
                      <Text size="xs" color="tertiary">
                        {formatBytes(file.size)}
                      </Text>
                    </Stack>
                  </Stack>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => setFile(null)}
                    disabled={busy}
                  >
                    <Icon name="X" size="sm" />
                  </Button>
                </Stack>
              ) : (
                <button
                  type="button"
                  disabled={busy}
                  onClick={() => fileInputRef.current?.click()}
                  onDragOver={(e) => {
                    e.preventDefault();
                    setDragOver(true);
                  }}
                  onDragLeave={() => setDragOver(false)}
                  onDrop={(e) => {
                    e.preventDefault();
                    setDragOver(false);
                    onPickFile(e.dataTransfer.files?.[0] ?? null);
                  }}
                  className={cn(
                    'flex w-full flex-col items-center justify-center gap-3 rounded-md border-2 border-dashed px-6 py-16 text-center transition-colors',
                    dragOver
                      ? 'border-bg-accent-strong-secondary bg-bg-secondary'
                      : errors.file
                        ? 'border-border-negative-subtle bg-bg-negative-subtle'
                        : 'border-border-tertiary bg-bg-primary',
                  )}
                >
                  <div className="flex size-11 items-center justify-center rounded-full border border-border-tertiary bg-bg-primary">
                    <Icon name="Upload" size="md" />
                  </div>
                  <Stack gap={0} align="center">
                    <Text weight="semibold">
                      Drop your{' '}
                      <Text inline className="font-mono">
                        .csv
                      </Text>{' '}
                      file here
                    </Text>
                    <Text size="sm" color="tertiary">
                      or{' '}
                      <Text inline weight="medium" className="underline">
                        browse files
                      </Text>
                    </Text>
                  </Stack>
                </button>
              )}
              <input
                ref={fileInputRef}
                type="file"
                accept=".csv"
                hidden
                onChange={(e) => onPickFile(e.target.files?.[0] ?? null)}
              />
              <Stack direction="horizontal" gap={2} align="center">
                <Text size="xs" color="tertiary">
                  Accepted format:
                </Text>
                <Text
                  inline
                  size="xs"
                  className="rounded-sm border border-border-tertiary bg-bg-secondary px-2 py-0.5 font-mono"
                >
                  .csv
                </Text>
              </Stack>
              {errors.file ? (
                <Stack
                  direction="horizontal"
                  gap={1}
                  align="center"
                  className="text-text-negative"
                >
                  <Icon name="TriangleAlert" size="xs" />
                  <Text size="xs" color="inherit">
                    Upload a CSV file to continue
                  </Text>
                </Stack>
              ) : null}
            </Stack>
          </WhiteCard>

          <Stack direction="horizontal" gap={2}>
            <Button onClick={onStart} disabled={busy}>
              {busy ? 'Starting…' : 'Start import'}
            </Button>
          </Stack>
        </Stack>

        <Stack gap={4} align="stretch">
          <WhiteCard>
            <Stack gap={3}>
              <Heading size="lg" weight="semibold">
                Start with Blank Template
              </Heading>
              <Button
                variant="outline"
                className="w-full justify-center"
                onClick={downloadTemplateCsv}
              >
                <Icon name="Download" size="sm" />
                Download {TEMPLATE_FILENAME}
              </Button>
            </Stack>
          </WhiteCard>

          <WhiteCard>
            <Stack gap={4}>
              <Stack gap={2}>
                <Heading size="lg" weight="semibold">
                  Required Columns
                </Heading>
                <Stack gap={1}>
                  {REQUIRED_COLUMNS.map((c) => (
                    <ColumnRow key={c.key} name={c.key} hint={c.hint} />
                  ))}
                </Stack>
              </Stack>
              <Separator />
              <Stack gap={2}>
                <Heading size="lg" weight="semibold">
                  Optional Columns
                </Heading>
                <Stack gap={1}>
                  {OPTIONAL_COLUMNS.map((c) => (
                    <ColumnRow key={c.key} name={c.key} hint={c.hint} />
                  ))}
                </Stack>
              </Stack>
            </Stack>
          </WhiteCard>
        </Stack>
      </div>
    </Stack>
  );
};
